/**
 * Clean one IdP-supplied name candidate into a greeting-safe first name.
 *
 * Keycloak-brokered accounts (the Teams embed path) often carry no
 * `given_name`, and their `name` falls back to the broker username, e.g.
 * "sso_patrick.quinn" or "sso_patrick.quinn@company.com". A raw username must
 * never surface as the member's name, so: strip the broker prefix, drop any
 * email domain, take the first username segment, and title-case it when it was
 * derived from a username shape (a pristine given name passes through
 * untouched).
 */
function cleanIdpNameCandidate(raw) {
    const trimmed = raw?.trim();
    if (!trimmed)
        return undefined;
    const unprefixed = trimmed.replace(/^sso[_-]/i, "").trim();
    const first = unprefixed.split(/\s+/)[0];
    if (!first)
        return undefined;
    // Dotted initials ("J.R.", "J.R") are a name, not a username: single letters
    // around the dots distinguish them from "patrick.quinn". Pass them through.
    if (/^(?:[a-z]\.)+[a-z]?\.?$/i.test(first))
        return first;
    // Hyphens are legitimate in given names (Jean-Pierre); dots, underscores,
    // and @ are not, so only those mark a username shape and split segments.
    const localPart = first.includes("@") ? first.slice(0, first.indexOf("@")) : first;
    const wasUsernameShaped = unprefixed !== trimmed || /[@._]/.test(first);
    const segment = localPart.split(/[._]/)[0];
    if (!segment)
        return undefined;
    // A numeric segment is an account id (Teams broker usernames are
    // "sso_<employeeId>"), never a name; reject it so callers fall through to
    // the next candidate instead of greeting the member by employee number.
    if (/^\d+$/.test(segment))
        return undefined;
    if (!wasUsernameShaped)
        return segment;
    return segment.charAt(0).toUpperCase() + segment.slice(1);
}
/** Resolve the first name HealthOS should use in greetings and plan copy. */
export function preferredFirstName(input) {
    // The onboarding "What should we call you?" answer is user-typed and wins
    // verbatim; only IdP-supplied candidates get username cleaning.
    const fromOnboarding = input.onboardingFirstName?.trim();
    if (fromOnboarding)
        return fromOnboarding.split(/\s+/)[0];
    const given = cleanIdpNameCandidate(input.givenName);
    if (given)
        return given;
    const full = cleanIdpNameCandidate(input.fullName);
    if (full)
        return full;
    // Fallback of last resort: the IdP's own display name, consulted only when
    // the standard claims yield nothing usable (the Teams embed case).
    return cleanIdpNameCandidate(input.displayName);
}
const stringClaim = (profile, key) => {
    const value = profile[key];
    return typeof value === "string" ? value : null;
};
/**
 * Resolve the first name straight from an OIDC profile's claims.
 *
 * The standard claims run first: given_name, then name. Keycloak-brokered
 * sessions (the Teams embed) carry neither in a usable form, but Keycloak's
 * SsoAttributesMapper imports the IdP's own userinfo as claims prefixed with
 * the IdP type, so the member's real name survives as azuread_displayName
 * (Microsoft) or sso_displayName (unknown IdP type); those are fallbacks,
 * consulted only when the standard claims yield nothing.
 */
export function preferredFirstNameFromProfile(profile) {
    return preferredFirstName({
        givenName: stringClaim(profile, "given_name"),
        fullName: stringClaim(profile, "name"),
        displayName: stringClaim(profile, "azuread_displayName") ?? stringClaim(profile, "sso_displayName"),
    });
}
//# sourceMappingURL=preferred-first-name.js.map